import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Box,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Custom theme with the provided colors
const theme = createTheme({
  palette: {
    primary: {
      main: "#3f434d",
      light: "#63c2e6",
    },
    secondary: {
      main: "#7cbf60",
      light: "#f1a94e",
    },
    neutral: {
      dark: "#191b20",
      main: "#000",
      light: "#fff",
    },
    accent: {
      main: "#9d7fe9",
      light: "#ff6b6b",
    },
  },
});

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.neutral.dark,
  padding: theme.spacing(2, 4),
  fontSize: "1.2rem",
  height: "56px", // Match the default height of the TextField
  "&:hover": {
    backgroundColor: theme.palette.secondary.light,
  },
}));

const SearchPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedHack, setSelectedHack] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [addressDetails, setAddressDetails] = useState([]);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const searchType = location.state?.searchType || "email";

  useEffect(() => {
    setSearchTerm("");
    setResults([]);
    setPage(1);
  }, [searchType]);

  const handleSearch = async () => {
    setError("");
    setIsLoading(true);

    try {
      let response;
      if (searchType === "email") {
        response = await axios.get(
          `https://api.hackerthread.com/api/people/search`,
          {
            params: { email1: searchTerm },
          }
        );
        setResults(processApiResponse(response.data));
      } else if (searchType === "phone") {
        if (!/^\d{10}$/.test(searchTerm)) {
          setError("Please enter a 10-digit phone number.");
          setIsLoading(false);
          return;
        }
        response = await axios.get(
          `https://api.hackerthread.com/api/phone/search`,
          {
            params: { phone_number: searchTerm },
          }
        );
        setResults(response.data);
      }
      setTotalPages(1); // Assuming all results are on one page
    } catch (error) {
      console.error("Error fetching search results:", error);
      setError("An error occurred while fetching results. Please try again.");
    }
    setIsLoading(false);
    setHasSearched(true);
  };

  const handleViewAddress = async (personId) => {
    setSelectedPersonId(personId);
    try {
      const response = await axios.get(
        `https://api.hackerthread.com/api/address/search`,
        {
          params: { person: personId },
        }
      );
      setAddressDetails(response.data.addressDetails || []);
      setAddressDialogOpen(true);
    } catch (error) {
      console.error("Error fetching address details:", error);
    }
  };

  const processApiResponse = (data) => {
    const processedResults = [];
    data.people.forEach((person) => {
      const personHacks = data.hackDetails[person.id] || {};
      Object.values(personHacks).forEach((hack) => {
        processedResults.push({
          name: person.name,
          email: person.email1,
          title: hack.title,
          type: hack.type,
          hackDate: hack.hack_date || "Date Unknown",
          leakDate: hack.leak_date || "Date Unknown",
          description: hack.description,
          riskLevel: hack.hack_info.risk_assessment,
          hackInfoId: hack.hack_info.id,
        });
      });
    });
    return processedResults;
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getRiskColor = (risk) => {
    const colors = [
      "#4caf50",
      "#4caf50",
      "#8bc34a",
      "#8bc34a",
      "#ffeb3b",
      "#ffc107",
      "#ff9800",
      "#f44336",
      "#f44336",
      "#b71c1c",
    ];
    return colors[risk - 1] || colors[0];
  };

  const handleBuyClick = (hackInfoId) => {
    setSelectedHack(hackInfoId);
    setOpenDialog(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="lg"
        sx={{ bgcolor: "neutral.dark", minHeight: "100vh", py: 4 }}
      >
        <Typography variant="h4" gutterBottom color="primary.main">
          {searchType === "email" ? "Email Search" : "Phone Search"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            mb: 4,
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", width: "100%", mb: 2 }}>
            <TextField
              variant="outlined"
              placeholder={`Enter ${searchType} to search`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                mr: 2,
                flexGrow: 1,
                maxWidth: "60%",
                "& .MuiOutlinedInput-root": { bgcolor: "neutral.light" },
              }}
              InputProps={{
                style: { height: "56px" },
              }}
            />
            <StyledButton
              onClick={handleSearch}
              disabled={isLoading}
              sx={{ flexGrow: 1 }}
            >
              Search
            </StyledButton>
          </Box>
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <Typography color="neutral.light">Loading...</Typography>
        ) : results.length > 0 ? (
          <List>
            {results.map((result, index) => (
              <ListItem
                key={index}
                alignItems="flex-start"
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  bgcolor: "neutral.light",
                }}
              >
                <ListItemText
                  primary={
                    <Typography color="accent.main" variant="h6">
                      {result.name}
                    </Typography>
                  }
                  secondary={
                    searchType === "email" ? (
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="neutral.dark"
                        >
                          Email: {result.email}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="body2"
                          color="neutral.dark"
                        >
                          Title: {result.title}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="body2"
                          color="neutral.dark"
                        >
                          Type: {result.type}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="body2"
                          color="neutral.dark"
                        >
                          Hack Date: {result.hackDate}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="body2"
                          color="neutral.dark"
                        >
                          Leak Date: {result.leakDate}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="body2"
                          color="neutral.dark"
                        >
                          {result.description}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        component="span"
                        variant="body2"
                        color="neutral.dark"
                      >
                        Phone: {result.phone_number}
                      </Typography>
                    )
                  }
                />
                {searchType === "phone" && (
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "accent.main",
                      color: "neutral.light",
                      "&:hover": { bgcolor: "accent.light" },
                    }}
                    onClick={() => handleViewAddress(result.people_id)}
                  >
                    View Address
                  </Button>
                )}
                {searchType === "email" && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      ml: 2,
                    }}
                  >
                    <Chip
                      label={`Risk: ${result.riskLevel}`}
                      sx={{
                        bgcolor: getRiskColor(result.riskLevel),
                        color: "neutral.light",
                        mb: 1,
                      }}
                    />
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "accent.main",
                        color: "neutral.light",
                        "&:hover": { bgcolor: "accent.light" },
                      }}
                      onClick={() => handleBuyClick(result.hackInfoId)}
                    >
                      Buy Report
                    </Button>
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        ) : hasSearched ? (
          <Typography color="neutral.light">
            No results found for the given {searchType}.
          </Typography>
        ) : null}

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle sx={{ bgcolor: "primary.main", color: "neutral.light" }}>
            Hack Info ID
          </DialogTitle>
          <DialogContent
            sx={{ bgcolor: "neutral.light", color: "neutral.dark" }}
          >
            <Typography>Hack Info ID: {selectedHack}</Typography>
          </DialogContent>
          <DialogActions sx={{ bgcolor: "neutral.light" }}>
            <Button
              onClick={() => setOpenDialog(false)}
              sx={{ color: "accent.main" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={addressDialogOpen}
          onClose={() => setAddressDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: "primary.main", color: "neutral.light" }}>
            Address Information
          </DialogTitle>
          <DialogContent
            sx={{ bgcolor: "neutral.light", color: "neutral.dark", pt: 2 }}
          >
            {addressDetails.length > 0 ? (
              addressDetails.map((address, index) => (
                <Box
                  key={index}
                  mb={2}
                  p={2}
                  bgcolor="rgba(0, 0, 0, 0.03)"
                  borderRadius={1}
                >
                  <Typography variant="h6" gutterBottom>
                    Address {index + 1}
                  </Typography>
                  <Typography>
                    <strong>Street:</strong> {address.street}
                  </Typography>
                  <Typography>
                    <strong>City:</strong> {address.city}
                  </Typography>
                  <Typography>
                    <strong>State:</strong> {address.state}
                  </Typography>
                  {address.zip && (
                    <Typography>
                      <strong>ZIP:</strong> {address.zip}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography>
                No address information found for this person.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: "neutral.light", p: 2 }}>
            <Button
              onClick={() => setAddressDialogOpen(false)}
              variant="contained"
              sx={{
                bgcolor: "accent.main",
                color: "neutral.light",
                "&:hover": { bgcolor: "accent.light" },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Box mt={4}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              color: "accent.main",
              borderColor: "accent.main",
              "&:hover": {
                backgroundColor: "accent.light",
                color: "neutral.light",
              },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default SearchPage;
